import React, { useState, useEffect } from "react";
import {
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  AppBar,
  Toolbar,
  Autocomplete,
  Icon,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import URLs from "constants/urls";
import Cookie from "js-cookie";
import Slider from "react-slick";
import { StyledSnackbar, StyledAlert } from "components/StyledComponents/styledComponents";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import image from "../../assets/landingimages/images/image.png";
import kotalogo from "../../assets/images/logos/kota-logo.png";
import { useMaterialUIController, setUser } from "context";
import { selectScrollStyle, CustomPaper } from "components/Styles/globalStyles";

function LoginPage() {
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [selectedDsaId, setSelectedDsaId] = useState("");
  const [dsaData, setDsaData] = useState([]);
  const navigate = useNavigate();
  const [controller, dispatch] = useMaterialUIController();

  useEffect(() => {
    fetchDsaMasterData();
  }, []);

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    autoplay: true,
    autoplaySpeed: 2000,
    pauseOnHover: true,
    responsive: [
      {
        breakpoint: 550,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
        },
      },
    ],
    customPaging: (i) => (
      <div style={{ color: "#ffffff", fontSize: "clamp(16px, 2vw, 20px)", textAlign: "center" }}>
        {"__"}
      </div>
    ),
  };

  const financeSlides = [
    {
      title: "Seamless Application Tracking",
      description:
        "Track all your loan, insurance, and mutual fund applications with ease and precision.",
    },
    {
      title: "Smart Financial Management",
      description: "Monitor and manage your financial inflows and outflows effectively.",
    },
    {
      title: "Detailed Monthly Insights",
      description: "Access comprehensive monthly financial reports and visual graphs.",
    },
    {
      title: "Streamlined Client Communication",
      description: "Enhance service quality with efficient client communication.",
    },
  ];

  const fetchDsaMasterData = async () => {
    const url = URLs.hrmsDsaLoginDetailsUrl;
    const options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    };
    try {
      const response = await fetch(url, options);
      const data = await response.json();
      const sortedData = data.sort((a, b) => a.dsaName.localeCompare(b.dsaName));

      setDsaData(sortedData);
    } catch (error) {
      setAlert("Failed to fetch company data", "error");
    }
  };

  const handleSetRememberMe = () => setRememberMe(!rememberMe);
  const handleClose = () => setOpen(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      setAlert("Please enter username and password", "error");
      return;
    }
    const hrmsLoginUrl = URLs.hrmsLoginUrl;
    const hrmsLoginCredentials = {
      employeeCode: username,
      employeePassword: password,
      companyId: selectedDsaId,
    };
    const defaultHrmsObject = {
      jwtToken: "",
      employeeCode: "",
      password: "",
      companyId: 0,
      employeeId: 0,
      employeeName: "",
      dsaId: 0,
      dsaCode: "",
      dsaName: "",
      menu: [],
    };
    try {
      // businessLogin
      const hrmsLoginResponse = await fetch(hrmsLoginUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(hrmsLoginCredentials),
      });
      const businessLoginResponse = await fetch(URLs.businessLogin, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(hrmsLoginCredentials),
      });

      if (!hrmsLoginResponse.ok) {
        const errorMsg = await hrmsLoginResponse.text();
        throw new Error(errorMsg || "Login failed. Please try again.");
      }
      if (!businessLoginResponse.ok) {
        const errorMsg = await businessLoginResponse.text();
        throw new Error(errorMsg || "Login failed. Please try again.");
      }
      const hrmsLoginObject = await hrmsLoginResponse.json();
      const businessLoginObject = await businessLoginResponse.text();
      const menuData = hrmsLoginObject.menu;
      function transformData(data) {
        const result = [];
        const groupedMap = new Map();
        data.forEach((item) => {
          const {
            mainMenuId,
            mainMenu,
            subMenuId,
            subMenuName,
            eouRoutingPath,
            type,
            menuKey,
            icon,
            component,
          } = item;

          if (!groupedMap.has(mainMenuId)) {
            groupedMap.set(mainMenuId, {
              mainMenuId,
              mainMenu,
              children: [],
            });
          }

          groupedMap.get(mainMenuId).children.push({
            subMenuId,
            subMenuName,
            eouRoutingPath,
            type,
            menuKey,
            icon,
            component,
          });
        });

        groupedMap.forEach((group) => {
          if (group.children.length === 1) {
            const child = group.children[0];
            result.push({
              type: "collapse",
              name: group.mainMenu,
              key: child.menuKey,
              route: child.eouRoutingPath,
              icon: <Icon fontSize="small">{child.icon}</Icon>,
              component: child.component,
            });
          } else {
            result.push({
              type: "dropdown",
              name: group.mainMenu,
              key: group.mainMenu.toLowerCase().replace(/\s+/g, "-"),
              icon: <Icon fontSize="small">{group.children[0].icon}</Icon>,
              collapse: [
                {
                  type: "collapse",
                  name: group.mainMenu,
                  key: group.mainMenu.toLowerCase().replace(/\s+/g, "-"),
                  collapse: group.children.map((child) => ({
                    type: "collapse",
                    name: child.subMenuName,
                    key: child.menuKey,
                    route: child.eouRoutingPath,
                    icon: <Icon fontSize="small">{child.icon}</Icon>,
                    component: child.component,
                  })),
                },
              ],
            });
          }
        });

        return result;
      }
      const transformedData = transformData(menuData);

      Cookie.set("access_token", "true", { expires: rememberMe ? 7 : undefined });
      Cookie.set("jwt_token", businessLoginObject, { expires: rememberMe ? 7 : undefined });
      Cookie.set("hrms_token", hrmsLoginObject.jwtToken, { expires: rememberMe ? 7 : undefined });
      const hrmsLoginObjectFormatted = {
        ...defaultHrmsObject,
        ...hrmsLoginObject,
        menu: [...transformedData],
      };
      setUser(dispatch, hrmsLoginObjectFormatted);
      navigate("/landing-page", { replace: true });
      setUsername("");
      setPassword("");
      setSelectedDsaId("");
      setAlert("Login successful!", "success");
    } catch (error) {
      setAlert(error.message, "error");
    }
  };

  const setAlert = (msg, type) => {
    setMessage(msg);
    setAlertType(type);
    setOpen(true);
  };

  const handleChange = (event, newValue) => {
    if (newValue) {
      setSelectedDsaId(newValue.dsaId);
    } else {
      setSelectedDsaId(null);
    }
  };

  const revolveOneKeyframes = {
    "0%": {
      transform: "rotate(0deg) translateX(clamp(100px, 15vw, 150px)) rotate(0deg)",
    },
    "50%": {
      transform: "rotate(180deg) translateX(clamp(100px, 15vw, 150px)) rotate(-180deg)",
    },
    "100%": {
      transform: "rotate(360deg) translateX(clamp(100px, 15vw, 150px)) rotate(-360deg)",
    },
  };

  const revolveTwoKeyframes = {
    "0%": {
      transform: "rotate(0deg) translateX(clamp(150px, 20vw, 200px)) rotate(0deg)",
    },
    "50%": {
      transform: "rotate(-180deg) translateX(clamp(150px, 20vw, 200px)) rotate(180deg)",
    },
    "100%": {
      transform: "rotate(-360deg) translateX(clamp(150px, 20vw, 200px)) rotate(-360deg)",
    },
  };

  return (
    <Grid
      container
      sx={{
        width: "100%",
        height: "100vh",
        background: "linear-gradient(to bottom right, #f5f7fa, rgb(192, 13, 13))",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        maxHeight: "100vh",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <Dialog
        open={loading}
        onClose={() => setLoading(false)}
        aria-labelledby="loading-dialog-title"
        sx={{
          "& .MuiDialog-paper": {
            padding: "clamp(10px, 2vw, 20px)",
            minWidth: "clamp(200px, 30vw, 300px)",
          },
        }}
      >
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "clamp(10px, 2vw, 20px)",
          }}
        >
          <CircularProgress color="primary" size="clamp(24px, 4vw, 40px)" />
          <Typography variant="h6" sx={{ fontSize: "clamp(14px, 2vw, 20px)" }}>
            Loading, please wait...
          </Typography>
        </DialogContent>
      </Dialog>
      <StyledSnackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        severity={alertType}
        sx={{
          width: "clamp(250px, 50vw, 400px)",
          top: "clamp(10px, 2vh, 20px)",
        }}
      >
        <StyledAlert onClose={handleClose} severity={alertType}>
          {message}
        </StyledAlert>
      </StyledSnackbar>

      {/* Left Side - Content */}
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: "flex",
          height: { xs: "50%", sm: "70%", md: "100%" },
          justifyContent: "center",
          flexDirection: "column",
          alignItems: "center",
          padding: "clamp(10px, 2vw, 20px)",
          background: "linear-gradient(135deg, #ffffff, rgb(250, 248, 248))",
          overflow: "hidden",
        }}
      >
        <Box
          component="img"
          src={kotalogo}
          alt="Brand Logo"
          sx={{
            height: { xs: "100px", sm: "140px", md: "clamp(180px, 18vh, 220px)" },
            width: "100%",
            maxWidth: "clamp(300px, 80vw, 500px)",
            objectFit: "contain",
            marginBottom: "clamp(20px, 3vh, 30px)",
          }}
        />

        <Box
          sx={{
            width: "100%",
            maxWidth: "clamp(300px, 60vw, 600px)",
            borderRadius: "clamp(10px, 2vw, 15px)",
            background: "linear-gradient(135deg,rgb(96, 16, 16), rgb(225, 2, 2))",
            padding: "clamp(15px, 3vw, 25px)",
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
            overflow: "hidden",
          }}
        >
          <AppBar
            position="static"
            sx={{
              background: "transparent",
              boxShadow: "none",
              padding: "clamp(5px, 1vw, 10px)",
            }}
          >
            <Toolbar sx={{ justifyContent: "center" }}>
              <Typography
                variant="h1"
                sx={{
                  fontWeight: "bold",
                  color: "#ffffff",
                  backgroundColor: "rgba(255,255,255,0.1)",
                  padding: "clamp(8px, 1.5vw, 25px)",
                  borderRadius: "clamp(6px, 1vw, 10px)",
                  textAlign: "center",
                  fontSize: { xs: "1rem", sm: "1.2rem", md: "clamp(1.5rem, 3vw, 2rem)" },
                  backdropFilter: "blur(5px)",
                }}
              >
                Fintrack
              </Typography>
            </Toolbar>
          </AppBar>

          <Slider
            {...sliderSettings}
            style={{
              marginTop: "clamp(15px, 2vh, 25px)",
              width: "100%",
              minWidth: "clamp(200px, 40vw, 300px)",
              maxWidth: "clamp(300px, 60vw, 500px)",
              margin: "auto",
            }}
          >
            {financeSlides.map((slide, index) => (
              <Box
                key={index}
                sx={{
                  textAlign: "center",
                  padding: "clamp(10px, 2vw, 15px)",
                  borderRadius: "clamp(6px, 1vw, 10px)",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    color: "#ffffff",
                    fontWeight: "600",
                    marginBottom: "clamp(8px, 1.5vh, 12px)",
                    fontSize: "clamp(16px, 2vw, 24px)",
                  }}
                >
                  {slide.title}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "rgba(255,255,255,0.9)",
                    lineHeight: 1.6,
                    fontSize: "clamp(12px, 1.5vw, 16px)",
                  }}
                >
                  {slide.description}
                </Typography>
              </Box>
            ))}
          </Slider>
        </Box>
      </Grid>

      {/* Right Side - Login Form with Circle Animation */}
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: "flex",
          height: { xs: "50%", sm: "70%", md: "100%" },
          justifyContent: "center",
          flexDirection: "column",
          alignItems: "center",
          padding: "clamp(10px, 2vw, 20px)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background Animation - Revolving Circles */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 0,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              width: { xs: "clamp(150px, 25vw, 200px)", md: "clamp(250px, 30vw, 300px)" },
              height: { xs: "clamp(150px, 25vw, 200px)", md: "clamp(250px, 30vw, 300px)" },
              borderRadius: "50%",
              border: "4px solid rgba(206, 9, 9, 0.6)",
              opacity: 0.6,
              animation: "revolveOne 6s infinite ease-in-out",
              "@keyframes revolveOne": revolveOneKeyframes,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: { xs: "clamp(200px, 35vw, 300px)", md: "clamp(300px, 40vw, 400px)" },
              height: { xs: "clamp(200px, 35vw, 300px)", md: "clamp(300px, 40vw, 400px)" },
              borderRadius: "50%",
              border: "4px solid rgba(255, 255, 255, 0.5)",
              opacity: 0.5,
              animation: "revolveTwo 8s infinite ease-in-out",
              "@keyframes revolveTwo": revolveTwoKeyframes,
            }}
          />
        </Box>

        <Paper
          elevation={8}
          sx={{
            borderRadius: "clamp(10px, 2vw, 15px)",
            padding: { xs: "clamp(15px, 3vw, 20px)", md: "clamp(25px, 4vw, 40px)" },
            width: "100%",
            maxWidth: "clamp(300px, 60vw, 450px)",
            background: "rgba(255, 255, 255, 0.95)",
            boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
            border: "1px solid rgba(139, 0, 0, 0.1)",
            position: "relative",
            zIndex: 1,
            transition: "transform 0.3s ease",
            "&:hover": {
              transform: "scale(1.02)",
            },
          }}
        >
          <Typography
            variant="h4"
            align="center"
            gutterBottom
            sx={{
              fontWeight: "bold",
              color: "#2b2d42",
              mb: "clamp(15px, 2vh, 25px)",
              fontSize: "clamp(20px, 3vw, 32px)",
            }}
          >
            Welcome
          </Typography>
          <form onSubmit={handleSubmit}>
            <Autocomplete
              options={dsaData}
              getOptionLabel={(option) => `${option.dsaName} (${option.dsaCode})`}
              onChange={handleChange}
              PaperComponent={CustomPaper}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select DSA"
                  variant="outlined"
                  sx={{
                    height: 43,
                    mb: 1,
                    "& .MuiInputBase-root": {
                      height: "100%",
                      minHeight: 43,
                    },
                  }}
                />
              )}
            />
            <TextField
              label="Username"
              variant="outlined"
              fullWidth
              margin="normal"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
            <TextField
              label="Password"
              type="password"
              variant="outlined"
              fullWidth
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <FormControlLabel
              control={<Checkbox checked={rememberMe} onChange={handleSetRememberMe} />}
              label="Remember Me"
              sx={{
                mb: "clamp(10px, 2vh, 20px)",
                color: "#2b2d42",
                "& .MuiTypography-root": {
                  fontSize: "clamp(12px, 1.5vw, 16px)",
                },
              }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{
                mt: "clamp(10px, 2vh, 20px)",
                py: "clamp(8px, 1.5vh, 15px)",
                borderRadius: "4 clamp(6px, 1vw, 8px) clamp(6px, 1vw, 8px) 0",
                backgroundColor: "#D32F2F",
                color: "#FFFFFF",
                "&:hover": {
                  backgroundColor: "#D32F2F",
                },
                textTransform: "none",
                fontSize: "clamp(14px, 1.5vw, 18px)",
                fontWeight: "bold",
                boxShadow: "0 4px 15px rgba(139,0,0,0.3)",
              }}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size="clamp(20px, 3vw, 24px)" sx={{ color: "#FFFFFF" }} />
              ) : (
                "Sign In"
              )}
            </Button>
          </form>
        </Paper>
      </Grid>

      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{
          position: "absolute",
          bottom: "clamp(10px, 2vh, 20px)",
          right: "clamp(180px, 12vw, 180px)",
          width: "100%",
        }}
      >
        <Box
          component="span"
          sx={{
            fontSize: "clamp(20px, 2vw, 20px)",
            color: "#555",
            marginRight: "8px",
            fontWeight: "bold",
          }}
        >
          Designed by
        </Box>

        <Box>
          <a href="https://www.sconexsoft.com" target="_blank" rel="noopener noreferrer">
            <Box
              component="img"
              src={image}
              alt="Brand Logo"
              sx={{
                height: {
                  xs: "32px",
                  sm: "48px",
                  md: "clamp(64px, 8vh, 80px)",
                },
                width: "100%",
                maxWidth: "clamp(120px, 30vw, 240px)",
                objectFit: "contain",
                cursor: "pointer",
              }}
            />
          </a>
        </Box>
      </Box>
    </Grid>
  );
}
export default LoginPage;
